import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidateComponentsCache, useGetComponent, useUpdateComponent } from 'src/api/components'
import { invalidateDocument } from 'src/api/document'
import { invalidatePositionCache } from 'src/api/positions'
import { FlexSpinner } from 'src/components/Spinner'
import { IComponent } from 'src/generated-types'
import { ShowDialogProps } from 'src/show-dialog'
import { ComponentForm } from './ComponentForm'

interface UpdateComponentProps extends ShowDialogProps {
  customerId: EntityId
  localityId: EntityId
  mooringId: EntityId
  positionId: EntityId
  id: EntityId
  isMultiPlacementsEnabled?: boolean
}

export const UpdateComponentDialog: React.FC<UpdateComponentProps> = ({
  pop,
  customerId,
  localityId,
  mooringId,
  positionId,
  id,
  isMultiPlacementsEnabled,
}) => {
  const { t } = useTranslation(['component', 'common'])

  const mutation = useUpdateComponent(customerId, positionId)
  const onSubmit = async form => {
    form.positionId = positionId
    if (form.overriddenLifetime == '') form.overriddenLifetime = 0
    if (form.minimumBreakingLoad == '') form.minimumBreakingLoad = 0

    await mutation.mutateAsync(form, {
      onSuccess: () => {
        toast.success(t('successfullyUpdated', { ns: 'common-api-message' }))
        invalidateComponentsCache.getComponent(customerId, positionId as EntityId, id)
        invalidateComponentsCache.getComponents(customerId, positionId as EntityId)
        invalidatePositionCache.getPositions(customerId, localityId, mooringId as EntityId)
        invalidateDocument.getDocuments(customerId)
        invalidatePositionCache.getBatchNumberDetailsByProductId(customerId, localityId, mooringId)
        invalidatePositionCache.getPosition(customerId, localityId, mooringId, positionId)
        pop()
      },
      onError: () => {
        toast.error(t('InternalError', { ns: 'common-api-message' }))
      },
    })
  }

  const { data, isLoading } = useGetComponent(customerId, positionId, id)

  if (isLoading) return <FlexSpinner />

  return (
    <ComponentForm
      title={t('updateComponent', { ns: 'component' })}
      onSubmit={onSubmit}
      onCancel={() => pop()}
      initialValues={data as IComponent}
      customerId={customerId}
      positionId={positionId}
      isLoading={isLoading}
      componentPlacement={data?.placement}
      isMultiPlacementsEnabled={isMultiPlacementsEnabled}
    />
  )
}
